import { LifeBuoyIcon, MessageSquareIcon } from 'lucide-react';
import { DataTable, type Column } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import { FigureBand } from '@/components/FigureBand';
import { PageIntro } from '@/components/PageIntro';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { PRIORITY_LABEL, STATUS_LABEL } from '@/features/support/SupportRequestPage';
import { fmtDateTime, fmtNumber, fmtRelative } from '@/lib/format';
import type { SupportRequestDto, SupportStatus } from '@/lib/types';
import { useQueryState } from '@/lib/url';
import { cn, initials } from '@/lib/utils';
import { useSupportRequests } from './queries';

const PAGE_SIZES = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

/**
 * Organization admins' list of help requests: how many are waiting, then every request with who sent it, what it
 * is about and where it stands. Each row opens the request on its own page (/org/support/:id) with the full
 * conversation, attachments, status and notes.
 */
export function SupportRequestsPage() {
  // The API pages this list; the page, rows per page and the status filter (shared with the table) live in the URL.
  const [sp, setSp] = useQueryState();
  const statusParam = sp.get('status');
  const status = statusParam && statusParam in STATUS_LABEL ? (statusParam as SupportStatus) : undefined;
  const page = Math.max(1, Number(sp.get('page')) || 1);
  const pageSize = PAGE_SIZES.includes(Number(sp.get('size'))) ? Number(sp.get('size')) : DEFAULT_PAGE_SIZE;
  const list = useSupportRequests({ status, page, pageSize });
  const counts = list.data?.counts;
  const waiting = counts ? counts.byStatus.new + counts.byStatus.in_progress : 0;
  const received = counts ? Object.values(counts.byStatus).reduce((n, c) => n + c, 0) : 0;

  const columns: Array<Column<SupportRequestDto>> = [
    {
      id: 'received',
      header: 'Received',
      sortValue: (r) => r.createdAt,
      className: 'w-[7rem]',
      cell: (r) => (
        <span className="text-muted-foreground whitespace-nowrap" title={fmtDateTime(r.createdAt)}>
          {fmtRelative(r.createdAt)}
        </span>
      ),
    },
    {
      id: 'subject',
      header: 'Request',
      sortValue: (r) => r.subject,
      searchValue: (r) => `${r.subject} ${r.message} ${r.reference}`,
      csv: (r) => r.subject,
      className: 'w-full max-w-0',
      cell: (r) => (
        <div className="min-w-0">
          <div className="truncate font-medium">{r.subject}</div>
          <div className="text-muted-foreground flex items-center gap-1.5 truncate text-xs">
            <span className="font-mono">{r.reference}</span>
            <span aria-hidden>·</span>
            <span>{r.categoryLabel}</span>
          </div>
        </div>
      ),
    },
    { id: 'reference', header: 'Reference', csv: (r) => r.reference, className: 'hidden', cell: () => null },
    { id: 'topic', header: 'Topic', csv: (r) => r.categoryLabel, className: 'hidden', cell: () => null },
    {
      id: 'from',
      header: 'From',
      sortValue: (r) => r.requester.name,
      searchValue: (r) => `${r.requester.name} ${r.requester.email}`,
      csv: (r) => `${r.requester.name} <${r.requester.email}>`,
      className: 'hidden w-[15rem] lg:table-cell',
      cell: (r) => (
        <div className="flex min-w-0 items-center gap-2.5">
          <Avatar className="size-7 shrink-0">
            <AvatarFallback className="text-[10px]">{initials(r.requester.name)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">{r.requester.name}</div>
            <div className="text-muted-foreground truncate text-xs">{r.requester.email}</div>
          </div>
        </div>
      ),
    },
    {
      id: 'activity',
      header: 'Replies',
      sortValue: (r) => r.lastMessageAt ?? r.createdAt,
      csv: (r) => r.messageCount ?? 0,
      className: 'hidden w-[9rem] xl:table-cell',
      cell: (r) => (
        <span className="text-muted-foreground flex items-center gap-1.5 text-xs tabular-nums" title={r.lastMessageAt ? `Last reply ${fmtDateTime(r.lastMessageAt)}` : 'No replies yet'}>
          <MessageSquareIcon className="size-3.5" aria-hidden /> {r.messageCount ?? 0}
          {r.lastMessageAt ? <span>· {fmtRelative(r.lastMessageAt)}</span> : null}
        </span>
      ),
    },
    {
      id: 'priority',
      header: 'Priority',
      sortValue: (r) => ({ high: 0, normal: 1, low: 2 })[r.priority],
      csv: (r) => PRIORITY_LABEL[r.priority],
      className: 'w-[6.5rem]',
      cell: (r) => <PriorityBadge priority={r.priority} />,
    },
    { id: 'status', header: 'Status', sortValue: (r) => r.status, csv: (r) => STATUS_LABEL[r.status], className: 'w-[7.5rem]', cell: (r) => <StatusBadge status={r.status} /> },
  ];

  return (
    <div className="space-y-5 sm:space-y-6">
      <PageIntro title="Help requests" description="Questions and problems your people sent from the Help page. Open one to reply, attach screenshots and track it to resolution." />

      <FigureBand
        ariaLabel="Help requests at a glance"
        figures={[
          { label: 'Waiting on you', value: counts ? fmtNumber(waiting) : undefined, bad: waiting > 0, hint: counts?.oldestOpenAt ? `Oldest ${fmtRelative(counts.oldestOpenAt)}` : 'Nothing open' },
          { label: 'New', value: counts ? fmtNumber(counts.byStatus.new) : undefined, hint: 'Not picked up yet' },
          { label: 'In progress', value: counts ? fmtNumber(counts.byStatus.in_progress) : undefined, hint: counts && counts.highOpen > 0 ? `${fmtNumber(counts.highOpen)} high priority open` : 'No high-priority requests' },
          { label: 'Resolved', value: counts ? fmtNumber(counts.byStatus.resolved + counts.byStatus.closed) : undefined, hint: counts ? `of ${fmtNumber(received)} received` : undefined },
        ]}
      />

      <DataTable
        data={list.data?.items}
        loading={list.isPending}
        error={list.isError ? list.error : undefined}
        onRetry={() => void list.refetch()}
        columns={columns}
        rowId={(r) => r.id}
        server={{ total: list.data?.total ?? 0, page, pageSize, onPageChange: (p) => setSp({ page: p > 1 ? p : null }), onPageSizeChange: (s) => setSp({ size: s === DEFAULT_PAGE_SIZE ? null : s, page: null }) }}
        pageSizes={PAGE_SIZES}
        rowHref={(r) => `/org/support/${r.id}`}
        searchPlaceholder="Search subject, requester, reference…"
        defaultSort={{ id: 'received', dir: 'desc' }}
        exportName="help-requests"
        filters={[
          { key: 'status', label: 'Status', options: (Object.keys(STATUS_LABEL) as Array<keyof typeof STATUS_LABEL>).map((s) => ({ value: s, label: STATUS_LABEL[s] })), predicate: (r, v) => r.status === v, counts: true },
          { key: 'priority', label: 'Priority', options: (Object.keys(PRIORITY_LABEL) as Array<keyof typeof PRIORITY_LABEL>).map((p) => ({ value: p, label: PRIORITY_LABEL[p] })), predicate: (r, v) => r.priority === v, counts: true },
        ]}
        emptyState={<EmptyState icon={LifeBuoyIcon} title="No help requests yet" description="Anyone on your team can send one from the Help link on the sign-in page or from their account menu." />}
        mobileCard={(r) => (
          <div className="grid gap-1.5">
            <div className="flex items-start justify-between gap-3">
              <span className="line-clamp-2 text-sm font-medium">{r.subject}</span>
              <StatusBadge status={r.status} />
            </div>
            <div className="text-muted-foreground flex flex-wrap items-center gap-x-1.5 text-xs">
              <span>{r.requester.name}</span>
              <span aria-hidden>·</span>
              <span>{r.categoryLabel}</span>
              <span aria-hidden>·</span>
              <span title={fmtDateTime(r.createdAt)}>{fmtRelative(r.createdAt)}</span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <PriorityBadge priority={r.priority} />
              <span className="text-muted-foreground flex items-center gap-1 text-xs tabular-nums">
                <MessageSquareIcon className="size-3.5" aria-hidden /> {r.messageCount ?? 0} {(r.messageCount ?? 0) === 1 ? 'reply' : 'replies'}
              </span>
            </div>
          </div>
        )}
      />
    </div>
  );
}

function PriorityBadge({ priority }: { priority: SupportRequestDto['priority'] }) {
  return (
    <Badge variant="outline" className={cn(priority === 'high' && 'border-destructive/40 text-destructive', priority === 'low' && 'text-muted-foreground')}>
      {PRIORITY_LABEL[priority]}
    </Badge>
  );
}

function StatusBadge({ status }: { status: SupportRequestDto['status'] }) {
  return <Badge variant={status === 'new' ? 'default' : status === 'in_progress' ? 'warning' : status === 'resolved' ? 'success' : 'muted'}>{STATUS_LABEL[status]}</Badge>;
}
